import { theme, useResponsive } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";

import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { useIntersection } from "~/lib/hooks";
import type { newsletterArticleArticle$key } from "~/queries/__generated__/newsletterArticleArticle.graphql";

import {
  AuthorDetail,
  Bottom,
  CommentTrigger,
  Container,
  Content,
  IframeContainer,
  NewsletterArticleWidgetContainer,
  Outer,
  ShareContainer,
  ShareWidget,
  SpeechPlayer,
  StyledNewsletterArticleContent,
  StyledNewsletterArticleHeader,
  StyledNewsletterArticleIssues,
  StyledNewsletterArticleWidget,
  StyledNewsletterArticleWidgetBottom,
  Topics,
} from "./styles";
import { Position } from "./widget";

export type Props = {
  reference: newsletterArticleArticle$key;
} & ArticleRenderChildProps;

export const NewsletterArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const article = useFragment(
    graphql`
      fragment newsletterArticleArticle on Article {
        ...contentNewsletterArticleArticle
        ...headerNewsletterArticleArticle
        ...issuesNewsletterArticleArticle
        ...widgetNewsletterArticleArticle
      }
    `,
    reference,
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false);
  const [isWidgetHidden, setIsWidgetHidden] = useState<boolean>(true);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState<boolean>(false);

  const { captureIntersectionElement: startDetector, intersection: startIntersection } =
    useIntersection({});
  const { captureIntersectionElement: endDetector, intersection: endIntersection } =
    useIntersection({});

  useEffect(() => {
    if (!isMobile) return;

    const endIntersectionBoundTop = endIntersection?.boundingClientRect?.top ?? -1;
    const startIntersectionBoundTop = startIntersection?.boundingClientRect?.top ?? 1;

    if (
      !endIntersection?.isIntersecting &&
      !startIntersection?.isIntersecting &&
      endIntersectionBoundTop > 0 &&
      startIntersectionBoundTop < 0
    ) {
      setIsWidgetHidden(false);
    } else {
      setIsWidgetHidden(true);
    }
  }, [isMobile, startIntersection, endIntersection]);

  return (
    <Outer>
      <Container>
        <StyledNewsletterArticleHeader provider={provider} reference={article} />
        <SpeechPlayer>{provider.speechPlayer({})}</SpeechPlayer>
        <Content>
          <div ref={startDetector} />
          <NewsletterArticleWidgetContainer>
            <StyledNewsletterArticleWidget
              isToggleSuccessMessage={isNewsletterSubscribed}
              position={Position.Sticky}
              reference={article}
              onSubscribeSuccess={() => {
                setIsNewsletterSubscribed(true);
              }}
            />
          </NewsletterArticleWidgetContainer>
          <IframeContainer>
            <StyledNewsletterArticleContent
              reference={article}
              onIframeLoaded={() => {
                setIsIframeLoaded(true);
              }}
            />
          </IframeContainer>
          <StyledNewsletterArticleIssues reference={article} />
          <div ref={endDetector} />
        </Content>
        {isIframeLoaded && (
          <Bottom>
            <ShareContainer>
              <CommentTrigger>{provider.commentTriggerAndReaction()}</CommentTrigger>
              <ShareWidget>
                {provider.shareWidget({
                  variant: "newsletter",
                })}
              </ShareWidget>
            </ShareContainer>
            <AuthorDetail>{provider.authorDetails({ firstNthAuthor: 1 })}</AuthorDetail>
            <Topics>{provider.topics()}</Topics>
          </Bottom>
        )}
      </Container>
      <StyledNewsletterArticleWidgetBottom
        $isHidden={isWidgetHidden || isNewsletterSubscribed}
        position={Position.Bottom}
        reference={article}
        onSubscribeSuccess={() => {
          setTimeout(() => setIsNewsletterSubscribed(true), 3000);
        }}
      />
    </Outer>
  );
};

NewsletterArticle.displayName = "NewsletterArticle";
